// Imports for external libraries go here.
import clsx from 'clsx';
import { FC, memo, useEffect } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { Container } from '@adobe/aem-react-editable-components';
import { setupLinkInterception, dispatchCustomEvent } from '@marriott/shared/mi-helper-utils';
import {
  useClientEnvVarsStore,
  useGlobalStore,
  useUserDetailsGlobalStore,
  getUserInfo,
} from '@marriott/mi-store-utils';
import { canUseDOM, Icon, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import { clearSessionTimeout, initializeSessionTimeout } from '../../../utils/sessionTimeoutHelper';
import { SignInDrawerWrapper } from '../../SignInDrawer/SignInDrawerWrapper';
import { LanguageSelectorWrapper } from '../LanguageSelector/LanguageSelectorWrapper';
import { MyTrips } from '../MyTrips';
import { UtilityLink } from '../UtilityLink';
import { StyledNavigationUtilityLink } from './NavigationUtilityLink.styles';
import { NavigationUtilityLinkProps, RestPropType, UtilityChildModel } from './NavigationUtilityLink.types';
import { getIconClass, isLongTextLocale, enableLoggedInPanel } from './helper';

import { useNavContext } from '../../../organisms/NavigationContainer';
import {
  ACCOUNT_AUTH_FLYOUT_MODAL_ID,
  ACCOUNT_FLYOUT_MODAL_ID,
  SIGN_IN_LINK_SUFFIX,
  PUBLIC_PAGES,
} from '../../../utils/constants';

// Memoized container for the account flyout datapagecomponent.
// Prevents header re-renders (scroll, hover, sticky) from cascading into
// the FlyoutDrawer tree, which causes mobile keyboard flickering.
const DataPageContainer = memo<{
  utilityNavigationItem: Record<string, unknown>;
  cqPath: string | undefined;
  itemKey: string;
  isAuthorMode: boolean;
}>(({ utilityNavigationItem, cqPath, itemKey, isAuthorMode }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ':type': _type, ...restProps } = utilityNavigationItem;
  const utilityItemPath = `${cqPath}/${itemKey}`;
  const childItemKey = (restProps as Record<string, unknown>)?.[':itemsOrder'] as string[] | undefined;
  const items = (restProps as Record<string, Record<string, unknown>>)?.[':items'];
  // Determine which flyout container to use based on isSignedInUser
  // This assumes isSignedInUser is available in the closure or passed as a prop/context
  // If not, you may need to pass it explicitly
  const isSignedInUser = useUserDetailsGlobalStore(state => state.isSignedInUser);

  const modalType = isSignedInUser ? ACCOUNT_AUTH_FLYOUT_MODAL_ID : ACCOUNT_FLYOUT_MODAL_ID;
  let childModel: UtilityChildModel | undefined = undefined;
  let flyoutKey: string | undefined = undefined;

  if (items) {
    // Try to find the correct flyout container and its key
    for (const [key, item] of Object.entries(items)) {
      if (item && (item as UtilityChildModel).id === modalType) {
        flyoutKey = key;
        childModel = {
          ...item,
          cqItems: (item as Record<string, unknown>)[':items'],
          cqItemsOrder: (item as Record<string, unknown>)[':itemsOrder'],
        } as UtilityChildModel;
        break;
      }
    }
    // Fallback: if no match, use the first available flyout container
    if (!childModel && Object.entries(items).length > 0) {
      const [firstKey, firstItem] = Object.entries(items)[0];
      flyoutKey = firstKey;
      if (firstItem && typeof firstItem === 'object') {
        childModel = {
          ...(firstItem as Record<string, unknown>),
          cqItems: (firstItem as Record<string, unknown>)[':items'],
          cqItemsOrder: (firstItem as Record<string, unknown>)[':itemsOrder'],
        } as UtilityChildModel;
      }
    }
  }

  const modelCqItems = childModel && flyoutKey ? { [flyoutKey]: childModel } : {};
  const modelCqItemsOrder = childModel && flyoutKey ? [flyoutKey] : [];

  const containerProps = {
    cqPath: utilityItemPath,
    cqItemsOrder: childItemKey,
    cqType: 'mi-aem-account/components/content/datapagecomponent',
    model: {
      ':type': 'mi-aem-account/components/content/datapagecomponent',
      id: itemKey || 'datapagecomponent',
      ':path': utilityItemPath,
      cqItems: modelCqItems,
      cqType: 'mi-aem-account/components/content/datapagecomponent',
      cqItemsOrder: modelCqItemsOrder,
    },
  };

  return <Container {...containerProps} model={containerProps?.model} isInEditor={!!isAuthorMode} isPage={false} />;
});

export const NavigationUtilityLink: FC<NavigationUtilityLinkProps> = props => {
  const {
    utilityNavigation,
    className,
    isRenderedInsideOverlay,
    logoUrl = '',
    logoImageAltText = '',
    logoType = '',
    cqPath,
    isAuthorMode,
    isBrandedVariation,
    showBonvoyLogo,
    isSimplifiedNavigation = false,
    featureFlagLegacyNav,
    isWhiteHeader,
    isTransparentHeader,
  } = props;
  const { ':itemsOrder': itemsOrder, ':items': items } = utilityNavigation;
  const { envVarsObject } = useClientEnvVarsStore();
  const locale = envVarsObject?.['ACCEPT_LANGUAGE'];
  const shouldOmitUtilityLabels = isLongTextLocale(locale) || isBrandedVariation;
  const shouldShowBonvoyLogo = showBonvoyLogo && isBrandedVariation;
  const isLegacyNavFlagEnabled = featureFlagLegacyNav?.[FEATURE_FLAGS.EXPERIENCE_MCOM_LEGACY_NAV];
  const { setIsSignInDrawerOpen, setIsAccountLoggedInPanelEnabled } = useNavContext();

  const navigationLinkResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/navigation-link'
    : 'mi-aem-common-spa/components/content/globalnav/navigation-link';

  const languageSelectorResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/language-selector'
    : 'mi-aem-common-spa/components/content/globalnav/language-selector';

  const myTripsResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/mytrips'
    : 'mi-aem-common-spa/components/content/globalnav/mytrips';

  const signInResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/signin'
    : 'mi-aem-common-spa/components/content/globalnav/signin';

  const isRememberedUser = useUserDetailsGlobalStore(state => state.isRememberMe);
  const isAccountFlyoutEnabled = utilityNavigation?.resourceProperties?.['enableAccountFlyout'] === 'true';

  useEffect(() => {
    const triggerFlyoutOnLoad = async () => {
      const urlParams = new URLSearchParams(window?.location?.search);
      const modalId = urlParams.get('modalId');
      const pathname = window?.location?.pathname;
      if (!modalId) return;

      // Wait for user details to be fetched (reuses in-flight request if one exists)
      try {
        await getUserInfo(true);
        // eslint-disable-next-line no-empty
      } catch {
        // If fetching user info fails, we still want to proceed with the flyout trigger logic using the existing user state.
      }
      const isUserSignedIn = useUserDetailsGlobalStore.getState().isSignedInUser;

      const isDefaultPage = pathname?.endsWith('/default.mi');
      const isPublicPage = PUBLIC_PAGES.some(page => pathname?.endsWith(page));

      const shouldDispatch =
        modalId === 'logout'
          ? !isUserSignedIn && isDefaultPage
          : modalId === 'sessionTimedOut'
          ? !isUserSignedIn && (isPublicPage || isDefaultPage)
          : true;
      if (shouldDispatch) {
        // Yield a frame so DataPageContainer re-renders with the updated isSignedInUser
        // before the event is dispatched, ensuring the correct flyout container is mounted.
        await new Promise(resolve => requestAnimationFrame(resolve));
        dispatchCustomEvent(false, modalId);
      }
      setIsSignInDrawerOpen(true);
    };

    if (document.readyState === 'complete') {
      triggerFlyoutOnLoad();
      return;
    }

    window.addEventListener('load', triggerFlyoutOnLoad);
    return () => window.removeEventListener('load', triggerFlyoutOnLoad);
  }, [setIsSignInDrawerOpen]);

  useEffect(() => {
    useGlobalStore.setState({ isAccountFlyoutEnabled });
  }, [isAccountFlyoutEnabled]);

  const currentUrl = canUseDOM ? window.location.href : undefined;
  const hasAccountLoggedInPanel = enableLoggedInPanel(items?.['utilityItem_5'], currentUrl);
  useEffect(() => {
    setIsAccountLoggedInPanelEnabled(hasAccountLoggedInPanel);
  }, [hasAccountLoggedInPanel]);

  const isDesktopAndAbove = useCheckBreakpoint('viewportL');

  const renderUtilityLink = (
    restProps: RestPropType,
    defaultIconClass: string,
    buttonText?: string,
    buttonType?: string
  ) => {
    const { labelTag: iconClassTag, 'cq:panelUrl': href, openInaNewTab, 'cq:panelTitle': linkText } = restProps;

    //TODO: Keeping the fallback class as globe as of now as currentlty no option from AEM to author icon. Later should be changed.
    const iconClass = getIconClass(iconClassTag || defaultIconClass);
    const target = openInaNewTab === 'true' ? '_blank' : '_self';
    const rel = openInaNewTab === 'true' ? 'noopener noreferrer' : undefined;
    return (
      <>
        <Icon
          iconClass={`icon-${iconClass} utility-icon custom_click_track`}
          ariaLabel={restProps['cq:panelTitle']}
          iconHref={href}
          target={target}
          rel={rel}
          enableLinkClsName
          isDecorative={true}
        >
          {href && (!shouldOmitUtilityLabels || buttonType === 'signin') && (
            <span
              aria-hidden="true"
              className={clsx('utility-link-text', {
                'd-none d-xl-inline-block': !isRenderedInsideOverlay,
              })}
            >
              {linkText || buttonText}
            </span>
          )}
        </Icon>
        {!href && (!shouldOmitUtilityLabels || buttonType === 'signin') && (
          <span
            aria-hidden="true"
            className={clsx('utility-link-text', {
              'd-none d-xl-inline-block': !isRenderedInsideOverlay,
              'd-lg-inline-block': buttonType === 'signin',
            })}
          >
            {linkText || buttonText}
          </span>
        )}
      </>
    );
  };

  useEffect(() => {
    initializeSessionTimeout(utilityNavigation?.resourceProperties?.['sessionTimeOutTimer']);

    return () => {
      clearSessionTimeout();
    };
  }, []);

  useEffect(() => {
    const { interceptAnchorClicks } = setupLinkInterception(SIGN_IN_LINK_SUFFIX, () => {
      dispatchCustomEvent(!isRememberedUser);
      setIsSignInDrawerOpen(true);
    });
    const cleanupFn = isAccountFlyoutEnabled && interceptAnchorClicks();
    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, [isAccountFlyoutEnabled, isRememberedUser]);

  return (
    <StyledNavigationUtilityLink
      className={clsx(className, 'pr-3 pr-md-5 pr-lg-0', {
        'align-items-baseline': isAuthorMode,
        'mt-lg-3 mt-md-3': isSimplifiedNavigation && !isRenderedInsideOverlay,
      })}
      isRenderedInsideOverLay={isRenderedInsideOverlay}
    >
      {/* this svg should be removed once the logo is put into the design system */}
      {shouldShowBonvoyLogo && (
        <li className={clsx('utility-item pt-1', { 'bonvoy-logo-desktop': isDesktopAndAbove })}>
          <a href="/default.mi">
            <div className="brand-logo-marriott-bonvoy-wordmark" aria-label="Marriott Bonvoy" />
          </a>
        </li>
      )}
      {itemsOrder.map((key: string) => {
        const utilityNavigationItem = items[key];

        const { ':type': type, ...restProps } = utilityNavigationItem;
        const trackingContentPosition = items?.['trackingProperties']?.trackingContentPosition;

        switch (type) {
          case navigationLinkResourceType:
            return (
              <li
                key={restProps['cq:panelTitle']}
                className={clsx(
                  'utility-item',
                  'utility-link',
                  { 't-label-inverse-xs d-none d-lg-block': !isRenderedInsideOverlay },
                  { 't-label-alt-xs': isRenderedInsideOverlay },
                  { 'color-scheme7': (isWhiteHeader && isBrandedVariation) || !isTransparentHeader }
                )}
              >
                <UtilityLink
                  restProps={{ trackingContentPosition, ...restProps }}
                  defaultIconClass="help"
                  isBrandedVariation={isBrandedVariation}
                  isRenderedInsideOverlay={isRenderedInsideOverlay}
                />
              </li>
            ); //later a seperate component for utility link can be created: <UtilityLink {...restProps} />; //Logic for Links such as help, covid-19

          case languageSelectorResourceType:
            return (
              <li
                key={restProps['cq:panelTitle'] || restProps?.resourceProperties['cq:panelTitle']}
                className={clsx(
                  'utility-item',
                  'utility-language-selector',
                  { 't-label-inverse-xs d-none d-lg-block': !isRenderedInsideOverlay },
                  { 't-label-alt-xs': isRenderedInsideOverlay }
                )}
              >
                <LanguageSelectorWrapper
                  restProps={{ trackingContentPosition, ...restProps }}
                  renderUtilityLink={renderUtilityLink}
                  buttonClassName={clsx('t-label-inverse-xs', {
                    'color-scheme7': (isBrandedVariation && isWhiteHeader) || !isTransparentHeader,
                  })}
                  cqPath={`${cqPath}/${key}`}
                  isAuthorMode={isAuthorMode}
                />
              </li>
            );

          case myTripsResourceType:
            return !isRenderedInsideOverlay ? (
              <li key={restProps['cq:panelTitle']} className={clsx('utility-item', 'utility-trips')}>
                <MyTrips
                  trackingContentPosition={trackingContentPosition}
                  restProps={restProps}
                  buttonClassName={clsx({
                    'color-scheme7': (isBrandedVariation && isWhiteHeader) || !isTransparentHeader,
                  })}
                >
                  {renderUtilityLink(restProps, 'nav---trips')}
                </MyTrips>
              </li>
            ) : null;

          case signInResourceType:
            return !isRenderedInsideOverlay ? (
              <li key={'signinbutton'} className={clsx('utility-item', 'utility-sigin')}>
                <SignInDrawerWrapper
                  logoUrl={logoUrl}
                  logoImageAltText={logoImageAltText}
                  logoType={logoType}
                  restProps={restProps}
                  renderUtilityLink={renderUtilityLink}
                  cqPath={`${cqPath}/${key}`}
                  isAuthorMode={isAuthorMode}
                  isBrandedVariation={isBrandedVariation}
                  isAccountFlyout={isAccountFlyoutEnabled}
                />
              </li>
            ) : null;

          // Accept both datapagecomponent and global-header-sign-in variants
          case 'mi-aem-account/components/content/datapagecomponent': {
            // Render the Container if not inside overlay and (account flyout is enabled OR in author mode)
            return !isRenderedInsideOverlay && (isAccountFlyoutEnabled || isAuthorMode) ? (
              <li key={key} id="utility-datapagecomponent" className={clsx('utility-datapagecomponent')}>
                <DataPageContainer
                  utilityNavigationItem={utilityNavigationItem}
                  cqPath={cqPath}
                  itemKey={key}
                  isAuthorMode={!!isAuthorMode}
                />
              </li>
            ) : null;
          }

          default:
            return null;
        }
      })}
    </StyledNavigationUtilityLink>
  );
};
